/**
 * Secure Admin Authentication Service
 * Handles admin login and session management with enhanced security
 */

import { supabase } from './supabaseClient.js';
import bcrypt from 'bcryptjs';

class SecureAdminAuthService {
  constructor() {
    this.currentUser = null;
    this.sessionKey = 'pgc_admin_session';
    this.loginAttempts = new Map(); // Track login attempts by IP
    this.maxLoginAttempts = 5;
    this.lockoutDuration = 15 * 60 * 1000; // 15 minutes
  }

  /**
   * Rate limiting for login attempts
   */
  isRateLimited(ipAddress) {
    if (!ipAddress) return false; // Skip rate limiting if no IP
    
    const attempts = this.loginAttempts.get(ipAddress) || { count: 0, firstAttempt: Date.now() };
    const now = Date.now();
    
    // Reset if lockout period has passed
    if (now - attempts.firstAttempt > this.lockoutDuration) {
      this.loginAttempts.delete(ipAddress);
      return false;
    }
    
    // Check if too many attempts
    if (attempts.count >= this.maxLoginAttempts) {
      return true; // Rate limited
    }
    
    return false;
  }

  /**
   * Record failed login attempt
   */
  recordFailedAttempt(ipAddress) {
    if (!ipAddress) return;
    
    const attempts = this.loginAttempts.get(ipAddress) || { count: 0, firstAttempt: Date.now() };
    attempts.count += 1;
    this.loginAttempts.set(ipAddress, attempts);
  }

  /**
   * Clear login attempts after successful login
   */
  clearLoginAttempts(ipAddress) {
    if (ipAddress) {
      this.loginAttempts.delete(ipAddress);
    }
  }

  /**
   * Authenticate admin user with enhanced security
   */
  async login(username, password, ipAddress = null) {
    // Rate limiting check
    if (this.isRateLimited(ipAddress)) {
      console.log(`Rate limited login attempt from IP: ${ipAddress}`);
      return {
        success: false,
        error: 'Too many failed login attempts. Please try again later.'
      };
    }

    try {
      console.log('Attempting secure login with email:', username);
      
      // Get admin user from database
      let userData = null;
      let userError = null;
      
      const { data: user, error: queryError } = await supabase
        .from('users')
        .select('id, username, email, password_hash, role, last_login, is_active')
        .eq('email', username)
        .eq('is_active', true)
        .single();
      
      if (queryError) {
        if (queryError.code === 'PGRST116') { // No rows returned
          // User not found - record failed attempt
          this.recordFailedAttempt(ipAddress);
          return {
            success: false,
            error: 'Invalid email or password'
          };
        } else {
          // Database error - might be RLS issue
          console.warn('Database query error:', queryError);
          // Try emergency credentials as fallback
          const emergencyCredentials = {
            'nfts2030@gmail.com': {
              password: 'R1712admin2019!',
              user: {
                id: '2',
                username: 'admin',
                email: 'nfts2030@gmail.com',
                role: 'admin',
                last_login: new Date().toISOString()
              }
            },
            'contacto@petgas.com.mx': {
              password: 'NyeaR[QcW;tP',
              user: {
                id: '1',
                username: 'diego',
                email: 'contacto@petgas.com.mx',
                role: 'admin',
                last_login: new Date().toISOString()
              }
            },
            'copd@petgas.com.mx': {
              password: '3m3ainffbd2a',
              user: {
                id: '3',
                username: 'oscar',
                email: 'copd@petgas.com.mx',
                role: 'admin',
                last_login: new Date().toISOString()
              }
            }
          };
          
          const emergencyUser = emergencyCredentials[username];
          if (emergencyUser && await this.verifyPassword(password, emergencyUser.password)) {
            console.log('Emergency authentication successful for:', username);
            userData = emergencyUser.user;
          } else {
            console.log('Emergency authentication failed');
            this.recordFailedAttempt(ipAddress);
            return {
              success: false,
              error: 'Invalid email or password'
            };
          }
        }
      } else {
        userData = user;
      }

      // Validate password (skip if using emergency fallback with plain text)
      if (userData.password_hash) {
        // Use bcrypt to compare the provided password with the stored hash
        const isPasswordValid = await this.verifyPasswordHash(password, userData.password_hash);
        console.log('Password validation result:', isPasswordValid);
        
        if (!isPasswordValid) {
          this.recordFailedAttempt(ipAddress);
          return {
            success: false,
            error: 'Invalid email or password'
          };
        }
      } else {
        // For emergency credentials, verify against plain text (for backward compatibility)
        if (userData.email === 'nfts2030@gmail.com' && password !== 'R1712admin2019!') {
          this.recordFailedAttempt(ipAddress);
          return {
            success: false,
            error: 'Invalid email or password'
          };
        } else if (userData.email === 'contacto@petgas.com.mx' && password !== 'NyeaR[QcW;tP') {
          this.recordFailedAttempt(ipAddress);
          return {
            success: false,
            error: 'Invalid email or password'
          };
        } else if (userData.email === 'copd@petgas.com.mx' && password !== '3m3ainffbd2a') {
          this.recordFailedAttempt(ipAddress);
          return {
            success: false,
            error: 'Invalid email or password'
          };
        }
      }

      // Update last_login
      try {
        await supabase
          .from('users')
          .update({ 
            last_login: new Date().toISOString(),
            last_login_ip: ipAddress || null
          })
          .eq('id', userData.id);
      } catch (updateError) {
        console.warn('Could not update last_login:', updateError);
        // Don't fail login just because we can't update last_login
      }

      // Clear successful login attempts
      this.clearLoginAttempts(ipAddress);
      
      // Set current user
      this.currentUser = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        role: userData.role || 'admin',
        lastLogin: userData.last_login
      };
      
      // Store session
      this.storeSession(this.currentUser);
      
      return {
        success: true,
        user: this.currentUser
      };
    } catch (error) {
      console.error('Login error:', error);
      this.recordFailedAttempt(ipAddress);
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  }

  /**
   * Hash a password
   */
  async hashPassword(password) {
    const saltRounds = 12;
    return await bcrypt.hash(password, saltRounds);
  }

  /**
   * Verify a password against a hash
   */
  async verifyPasswordHash(password, hash) {
    return await bcrypt.compare(password, hash);
  }

  /**
   * Verify plain text password (for emergency credentials)
   */
  async verifyPassword(plainPassword, expectedPassword) {
    return plainPassword === expectedPassword;
  }

  /**
   * Logout admin user
   */
  logout() {
    this.currentUser = null;
    localStorage.removeItem(this.sessionKey);
    sessionStorage.removeItem(this.sessionKey);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    if (this.currentUser) {
      return true;
    }
    
    // Check stored session
    const session = this.getStoredSession();
    if (session && this.isSessionValid(session)) {
      this.currentUser = session.user;
      return true;
    }
    
    return false;
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    if (!this.isAuthenticated()) {
      return null;
    }
    return this.currentUser;
  }

  /**
   * Store session in localStorage with additional security
   */
  storeSession(user) {
    const session = {
      user: {
        id: user.id,
        username: user.username,
        email: user.email,
        role: user.role
      },
      timestamp: Date.now(),
      expiresAt: Date.now() + (2 * 60 * 60 * 1000) // 2 hours
    };
    
    localStorage.setItem(this.sessionKey, JSON.stringify(session));
  }

  /**
   * Get stored session
   */
  getStoredSession() {
    try {
      const stored = localStorage.getItem(this.sessionKey);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error parsing stored session:', error);
      return null;
    }
  }

  /**
   * Check if session is valid
   */
  isSessionValid(session) {
    if (!session || !session.expiresAt) {
      return false;
    }
    
    return Date.now() < session.expiresAt;
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats() {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('Not authenticated');
      }

      // Get basic counts
      const { count: totalCount } = await supabase
        .from('whitelist_entries')
        .select('*', { count: 'exact', head: true });

      const today = new Date().toISOString().split('T')[0];
      const { count: todayCount } = await supabase
        .from('whitelist_entries')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today);

      const { count: emailsSent } = await supabase
        .from('whitelist_entries')
        .select('*', { count: 'exact', head: true })
        .eq('email_sent', true);

      const { count: emailsPending } = await supabase
        .from('whitelist_entries')
        .select('*', { count: 'exact', head: true })
        .eq('email_sent', false);

      // Get recent registrations
      const { data: recentData } = await supabase
        .from('whitelist_entries')
        .select('id, name, email, wallet_address, created_at, email_sent')
        .order('created_at', { ascending: false })
        .limit(10);

      const data = {
        total_registrations: totalCount || 0,
        today_registrations: todayCount || 0,
        emails_sent: emailsSent || 0,
        emails_pending: emailsPending || 0,
        recent_registrations: recentData || []
      };

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Get dashboard stats error:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch statistics'
      };
    }
  }

  /**
   * Get whitelist entries with pagination
   */
  async getWhitelistEntries(page = 1, limit = 50, search = '') {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('Not authenticated');
      }

      let query = supabase
        .from('whitelist_entries')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      // Add search filter
      if (search) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,wallet_address.ilike.%${search}%`);
      }

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error('Get whitelist entries error:', error);
        throw new Error('Failed to fetch whitelist entries');
      }

      return {
        success: true,
        data: data || [],
        pagination: {
          page,
          limit,
          total: count || 0,
          totalPages: Math.ceil((count || 0) / limit)
        }
      };
    } catch (error) {
      console.error('Get whitelist entries error:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch entries'
      };
    }
  }

  /**
   * Export whitelist entries to CSV
   */
  async exportToCSV() {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase
        .from('whitelist_entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error('Failed to fetch data for export');
      }

      // Convert to CSV
      const headers = ['ID', 'Name', 'Email', 'Wallet Address', 'Created At', 'Email Sent', 'IP Address'];
      const csvContent = [
        headers.join(','),
        ...data.map(entry => [
          entry.id,
          `"${entry.name.replace(/"/g, '""')}"`, // Escape quotes in names
          entry.email,
          entry.wallet_address,
          entry.created_at,
          entry.email_sent,
          entry.ip_address || ''
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `pgc-whitelist-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { success: true };
    } catch (error) {
      console.error('Export error:', error);
      return {
        success: false,
        error: error.message || 'Export failed'
      };
    }
  }

  /**
   * Delete whitelist entry
   */
  async deleteEntry(entryId) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('Not authenticated');
      }

      const { error } = await supabase
        .from('whitelist_entries')
        .delete()
        .eq('id', entryId);

      if (error) {
        throw new Error('Failed to delete entry');
      }

      return { success: true };
    } catch (error) {
      console.error('Delete entry error:', error);
      return {
        success: false,
        error: error.message || 'Delete failed'
      };
    }
  }

  /**
   * Update email sent status
   */
  async updateEmailStatus(entryId, emailSent) {
    try {
      if (!this.isAuthenticated()) {
        throw new Error('Not authenticated');
      }

      const { error } = await supabase
        .from('whitelist_entries')
        .update({ email_sent: emailSent })
        .eq('id', entryId);

      if (error) {
        throw new Error('Failed to update email status');
      }

      return { success: true };
    } catch (error) {
      console.error('Update email status error:', error);
      return {
        success: false,
        error: error.message || 'Update failed'
      };
    }
  }
}

// Create singleton instance
const secureAdminAuthService = new SecureAdminAuthService();

export default secureAdminAuthService;